import { Button, HStack, Link, Text, useToast, Flex } from "@chakra-ui/react"
import format from "date-fns/format"
import React, { useMemo } from "react"
import { NavLink, useNavigate } from "react-router-dom"
import { ExtendedPhoneDeviceFragment, PhoneDeviceStatus, useDeletePhoneDeviceMutation, useMeQuery } from "../../graphql"
import { checkForPermissions } from "../../utils"
import { Card, Stat } from "../common"

export type PhoneDeviceDetailsProps = {
	phoneDevice: ExtendedPhoneDeviceFragment
}

export const PhoneDeviceDetails: React.FC<PhoneDeviceDetailsProps> = ({ phoneDevice }) => {
	const [{ data: meData }] = useMeQuery()
	const [{ fetching: deleting }, deletePhoneDevice] = useDeletePhoneDeviceMutation()

	const toast = useToast()
	const navigate = useNavigate()

	const canUpdate = useMemo(() => checkForPermissions(meData?.me, "phone-devices/update-phone-device")[0], [meData?.me])
	const canDelete = useMemo(() => checkForPermissions(meData?.me, "phone-devices/delete-phone-device")[0], [meData?.me])
	const canUnassign = useMemo(() => checkForPermissions(meData?.me, "phone-devices/unassign-phone-device")[0], [meData?.me])

	const handleDelete = async () => {
		const shouldDelete = window.confirm("Are you sure you want to delete this phone device?")
		if (!shouldDelete) return

		const { data, error } = await deletePhoneDevice({ phoneDeviceId: phoneDevice._id })

		if (error) {
			toast({ description: error.message.replace("[GraphQL] ", ""), status: "error" })
			return
		}

		if (data?.deletePhoneDevice) {
			toast({ description: "Phone device deleted successfully", status: "success" })
			navigate("/phoneDevices", { replace: true })
			return
		}

		toast({ description: "Couldn't delete the phone device", status: "error" })
	}

	return (
		<Card
			title="Details"
			alwaysShowAction
			action={
				<HStack>
					{canUpdate && (
						<Button as={NavLink} to={`/phoneDevices/${phoneDevice._id}/update`} colorScheme="primary" size="sm">
							Update
						</Button>
					)}
					{canUnassign && phoneDevice.assignedToUserId && (
						<Button as={NavLink} to={`/users/${phoneDevice.assignedToUserId}/unassignPhoneDevice`} size="sm">
							Unassign
						</Button>
					)}
					{canDelete && (
						<Button colorScheme="error" size="sm" isLoading={deleting} onClick={handleDelete}>
							Delete
						</Button>
					)}
				</HStack>
			}
		>
			<Flex justify="center" align="center" flexWrap="wrap">
				<Stat label="Name" value={phoneDevice.label.name} />
				<Stat label="ID" value={phoneDevice.deviceId} />
				<Stat
					label="Status"
					value={
						phoneDevice.status === PhoneDeviceStatus.Assigned ? (
							<Text color="success.600">Assigned</Text>
						) : phoneDevice.status === PhoneDeviceStatus.Inactive ? (
							<Text color="error.600">Inactive</Text>
						) : (
							phoneDevice.status
						)
					}
				/>
				<Stat
					label="User"
					value={
						phoneDevice.user ? (
							<Link as={NavLink} to={`/users/${phoneDevice.user._id}`}>
								{phoneDevice.user.name}
							</Link>
						) : (
							<Text color="grayscale.label">Not assigned</Text>
						)
					}
				/>
				<Stat label="Assigned At" value={phoneDevice.assignedAt ? format(new Date(phoneDevice.assignedAt), "MMM dd, yyyy HH:mm") : "Not available"} />
				<Stat label="Created" value={format(new Date(phoneDevice.createdAt), "MMM dd, yyyy HH:mm")} />
				<Stat label="Updated" value={format(new Date(phoneDevice.updatedAt), "MMM dd, yyyy HH:mm")} />
			</Flex>
		</Card>
	)
}
